// Kanonische Produkttyp-Taxonomie (Single Source of Truth)
//
// Vereinheitlicht die zuvor über audio.ts (ProductCategory) und
// video.ts (VideoCategory) fragmentierten und inkonsistenten Werte.
// Alle Werte kebab-case.
//
// ⚠️ Hier stand bis 2026-09-02 eine ZUSICHERUNG über den Dateninhalt: „Daten-sicher verifiziert:
// in der DB existiert nur metadata.productCategory='silent-subliminal', keine Video-Daten, keine
// PascalCase-Werte." Zwei ihrer drei Aussagen waren beim Nachmessen falsch — es gibt sieben
// verschiedene Werte, und alle vier VIDEO-Werke tragen einen. Nur „keine PascalCase-Werte" hielt.
//
// Der Schaden war nicht der Irrtum, sondern der TON: der Satz klang geprüft, und einen geprüften
// Satz misst niemand nach. Eine Zusicherung über Dateninhalt, die im Quelltext steht, veraltet
// still — die Daten ändern sich, die Zeile nicht.
//
// Deshalb hier keine neue Zusicherung, sondern die MESSANWEISUNG. Sie veraltet nicht; wer sie
// ausführt, bekommt den heutigen Stand statt eines Standes von damals:
//
//     SELECT COALESCE(metadata->>'productCategory','(Feld fehlt)') AS wert, count(*)
//     FROM public.content GROUP BY 1 ORDER BY 2 DESC;
//
// Stand 2026-09-02 (dev, 15 Werke): 14 tragen das Feld, 1 nicht. Vorkommende Werte —
// ultra-subliminal 3 · audio-subliminal 2 · vision-movie 2 · silent-subliminal 2 ·
// subliminal-flash 2 · mind-book 1 · subliminal 2 (!) · Feld fehlt 1.
//
// ⚠️ `subliminal` steht NICHT in diesem Enum und stammt aus dem INGEST, nicht aus dem CMS:
// derselbe Wert liegt bei denselben Werken auch in `metadata.speicher.produktart`. Wer diesen
// Vertrag durchsetzen will, muss es beim Ingest tun — das Enum hier beschreibt, was gelten SOLL,
// und wird beim Schreiben nirgends geprüft (BACKLOG).
//
// Hinweis: product.ts/ProductCategory bleibt LEGACY (gehört zum toten
// ProductDashboard) und nutzt diese kanonische Quelle NICHT.

export enum ProductType {
  ULTRA_SUBLIMINAL = 'ultra-subliminal',
  AUDIO_SUBLIMINAL = 'audio-subliminal',
  SILENT_SUBLIMINAL = 'silent-subliminal',
  MIND_BOOK = 'mind-book',
  SUBLIMINAL_FLASH = 'subliminal-flash',
  VISION_MOVIE = 'vision-movie',
}

export const ProductTypeLabels: Record<ProductType, string> = {
  [ProductType.ULTRA_SUBLIMINAL]: 'Ultra Subliminal',
  [ProductType.AUDIO_SUBLIMINAL]: 'Audio Subliminal',
  [ProductType.SILENT_SUBLIMINAL]: 'Silent Subliminal',
  [ProductType.MIND_BOOK]: 'MindBook',
  [ProductType.SUBLIMINAL_FLASH]: 'Subliminal Flash',
  [ProductType.VISION_MOVIE]: 'Vision Movie',
};

// Medienformat-Mapping: ordnet jedem Produkttyp sein Medium zu.
//
// Der `Record<ProductType, …>` ist der Grund, warum diese Tabelle die Quelle ist und nicht
// die beiden Listen darunter: er zwingt jeden neuen Enum-Wert dazu, sich hier einzutragen —
// ein vergessener Typ ist ein Übersetzungsfehler, keine stille Lücke in einem Filter.
// Bewusst nicht exportiert: nach außen sind die beiden abgeleiteten Listen die Schnittstelle.
// Als Export wäre die Tabelle eine zweite Art, dieselbe Frage zu stellen — und der Weg,
// auf dem irgendwann wieder eine dritte Liste von Hand daneben entsteht.
const ProductTypeMedium: Record<ProductType, 'audio' | 'video'> = {
  [ProductType.ULTRA_SUBLIMINAL]: 'audio',
  [ProductType.AUDIO_SUBLIMINAL]: 'audio',
  [ProductType.SILENT_SUBLIMINAL]: 'audio',
  [ProductType.MIND_BOOK]: 'audio',
  [ProductType.SUBLIMINAL_FLASH]: 'video',
  [ProductType.VISION_MOVIE]: 'video',
};

// Die beiden Listen standen zuvor von Hand daneben und wiederholten dieselbe Zuordnung ein
// zweites Mal — zwei Stellen, die auseinanderlaufen können, ohne dass es jemandem auffällt.
// Jetzt fallen sie aus der Tabelle. `Object.values` liefert die Werte eines String-Enums in
// Deklarations-Reihenfolge; die Reihenfolge der Auswahlfelder bleibt damit unverändert.
const typesWithMedium = (medium: 'audio' | 'video'): ProductType[] =>
  Object.values(ProductType).filter((typ) => ProductTypeMedium[typ] === medium);

export const audioProductTypes = typesWithMedium('audio');

/**
 * Was das AUDIO-Formular zur Auswahl stellt — die Audio-Produktarten OHNE MindBook.
 *
 * ⚠️ Warum eine eigene Liste und nicht die Tabelle geändert: `ProductTypeMedium` ordnet
 * `MIND_BOOK` dem Medium `audio` zu, und das ist RICHTIG — ein MindBook IST Ton. Die Taxonomie
 * bleibt deshalb unangetastet (Tom-Entscheid 2026-09-01); geändert wird nur, was ein bestimmtes
 * FORMULAR anbietet. Wer die Tabelle anfasst, trifft auch Player, Suche und Katalog-Zuordnung.
 *
 * ⚠️ Und bewusst NICHT für die Normalisierung eines Bestandswerts verwendet
 * (`AudioForm.normalisiereProduktkategorie`): die liest weiter aus `audioProductTypes`. Sonst
 * verlöre ein Werk, das `mind-book` trägt, beim Öffnen des Formulars seine Produktart — die
 * Auswahl nicht anzubieten ist etwas anderes, als einen vorhandenen Wert nicht zu erkennen.
 * (Gemessen am 2026-09-01: **0 von 9** Audio-Werken tragen `mind-book`. Der Schutz steht trotzdem;
 * eine 0 von heute ist keine Zusage für morgen.)
 *
 * MindBooks werden unter „MindBooks" gepflegt — eigenes Formular, eigene Felder (zwei
 * Ton-Fassungen, Kapitel-Marker), eigene Route.
 */
export const audioFormularProduktarten = audioProductTypes.filter(
  (typ) => typ !== ProductType.MIND_BOOK,
);

export const videoProductTypes = typesWithMedium('video');
